import { useLiveQuery } from "@tanstack/react-db"
import {
	type RegisteredRouter,
	type RouteIds,
	useMatches,
} from "@tanstack/react-router"
import { Suspense, useCallback, useState } from "react"
import { useNotificationTapInProgress } from "@/app/notification-taps"
import { usePopup } from "@/entities/bottom-popup"
import {
	getNotificationRenderer,
	isShownOnScreen,
	type NotificationRecord,
	notificationCollection,
} from "@/entities/notification"
import { OverlayHost } from "@/shared/ui/overlay"

const HOME_SCREEN_ROUTE_IDS: ReadonlyArray<
	RouteIds<RegisteredRouter["routeTree"]>
> = ["/_authenticated/home/", "/_authenticated/home/new"]

function isDrawable(
	record: NotificationRecord,
	onHomeScreen: boolean,
): boolean {
	if (getNotificationRenderer(record.type) === undefined) return false
	return isShownOnScreen(record, onHomeScreen)
}

function headOf(
	queued: NotificationRecord[],
	onHomeScreen: boolean,
): NotificationRecord | null {
	for (const record of queued) {
		if (isDrawable(record, onHomeScreen)) return record
	}
	return null
}

function stackIdOf(record: NotificationRecord | null): string | null {
	if (record === null) return null
	return record.stackId
}

function RecordView({ record }: { record: NotificationRecord }) {
	const Renderer = getNotificationRenderer(record.type)
	if (Renderer === undefined) return null
	return <Renderer key={record.stackId} notification={record} />
}

// `displayed` отстаёт от головы live query на время exit-анимации: после dismiss
// запись уже удалена из коллекции, но её иммутабельный снапшот дорисовывается,
// пока шит уезжает вниз (OverlayHost open=false). Затем displayed сбрасывается
// и следующая запись (если есть) монтируется со своей enter-анимацией.
function NotificationOverlay() {
	const { data: queued } = useLiveQuery({
		query: (q) =>
			q
				.from({ n: notificationCollection })
				.orderBy(({ n }) => n.createdAt, "desc"),
	})
	const onHomeScreen = useMatches({
		select: (matches) =>
			matches.some((match) => HOME_SCREEN_ROUTE_IDS.includes(match.routeId)),
	})
	const head = headOf(queued, onHomeScreen)

	const popupActive = usePopup() !== null
	const tapInProgress = useNotificationTapInProgress()
	const [displayed, setDisplayed] = useState<NotificationRecord | null>(null)
	const handleExitComplete = useCallback(() => setDisplayed(null), [])

	if (popupActive || tapInProgress || displayed === null) {
		if (stackIdOf(displayed) !== stackIdOf(head)) setDisplayed(head)
		return null
	}

	const open = displayed.stackId === stackIdOf(head)

	return (
		<Suspense>
			<OverlayHost open={open} onExitComplete={handleExitComplete}>
				<RecordView record={displayed} />
			</OverlayHost>
		</Suspense>
	)
}

export { NotificationOverlay }
